import PageHeading from './PageHeading';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Datetime from 'react-datetime';
import moment from 'moment';
import 'react-datetime/css/react-datetime.css';
import LogoTray from './LogoTray';
import DeleteAccount from './DeleteAccount';
import '../Search.css';
import './Postavke.css';
import { InfoPopover } from './CompactControls';
import { PASSWORD_HELP_TEXT, validatePassword } from '../utils/passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || "/api";

function Postavke() {
  const [activeSection, setActiveSection] = useState('korisnicki');
  const [status, setStatus] = useState('');
  const [multiKeywordEnabled, setMultiKeywordEnabled] = useState(false);
  const [searchAllEnabled, setSearchAllEnabled] = useState(false);

  // Form states
  const [newName, setNewName] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [gender, setGender] = useState('');

  // Toggle states - load from user data in localStorage
  const [showForeignNews, setShowForeignNews] = useState(false);
  const [availableNewsCountries, setAvailableNewsCountries] = useState([]);
  const [enabledNewsCountries, setEnabledNewsCountries] = useState([]);
  const [savingNewsPreference, setSavingNewsPreference] = useState('');
  const [showSubcategories, setShowSubcategories] = useState(false);
  const [showSubcategoryOnArticles, setShowSubcategoryOnArticles] = useState(true);
  const [showPortalOnSubcategories, setShowPortalOnSubcategories] = useState(true);

  useEffect(() => {
    const fetchUserSettings = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/user/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setMultiKeywordEnabled(data.multi_keyword_enabled ?? false);
          setSearchAllEnabled(data.search_all_enabled ?? false);
          setShowForeignNews(data.show_foreign_news ?? false);
          setAvailableNewsCountries(data.available_news_countries || []);
          setEnabledNewsCountries(data.enabled_news_countries || []);
          setShowSubcategories(data.show_subcategories || false);
          setShowSubcategoryOnArticles(data.show_subcategory_on_articles ?? true);
          setShowPortalOnSubcategories(data.show_portal_on_subcategories ?? true);
          setNewName(data.name || '');
          setNewPhone(data.phone || '');
          setGender(data.gender || '');
          setBirthDate(data.birth_date ? moment(data.birth_date, 'YYYY-MM-DD') : null);
        }
      } catch {
        // ignore
      }
    };
    fetchUserSettings();
  }, []);

  const getToken = () => localStorage.getItem('token');

  const showStatus = (msg, isError = false) => {
    setStatus(msg);
    setTimeout(() => setStatus(''), 3000);
    if (isError) return;
  };

  const apiCall = async (endpoint, body) => {
    const response = await fetch(`${API_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    });
    if (response.ok) localStorage.setItem('settingsRevision', String(Date.now()));
    return response;
  };

  const handleUserDataSubmit = async (e) => {
    e.preventDefault();
    const formattedBirthDate =
      birthDate && typeof birthDate.format === 'function'
        ? birthDate.format('YYYY-MM-DD')
        : (typeof birthDate === 'string' ? birthDate : null);
    try {
      const response = await apiCall('update-user-data', {
        name: newName,
        birthDate: formattedBirthDate,
        phone: newPhone,
        gender,
      });
      if (response.ok) {
        showStatus('Postavke su uspješno spremljene.');
      } else {
        const error = await response.json();
        showStatus(error.error || 'Greška prilikom spremanja postavki.', true);
      }
    } catch {
      showStatus('Greška prilikom spremanja postavki.', true);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showStatus('Nova lozinka i potvrda se ne podudaraju!', true);
      return;
    }
    const passwordPolicyError = validatePassword(newPassword);
    if (passwordPolicyError) {
      showStatus(passwordPolicyError, true);
      return;
    }
    try {
      const response = await apiCall('update-password', { currentPassword, newPassword });
      if (response.ok) {
        showStatus('Lozinka je uspješno promijenjena.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const error = await response.json();
        showStatus(error.error || 'Greška prilikom promjene lozinke.', true);
      }
    } catch {
      showStatus('Greška prilikom promjene lozinke.', true);
    }
  };

  const handleForeignNewsToggle = async () => {
    setSavingNewsPreference('master');
    const newValue = !showForeignNews;
    try {
      const response = await apiCall('update-display-settings', { show_foreign_news: newValue });
      if (!response.ok) throw new Error('Save failed');
      setShowForeignNews(newValue);
      showStatus(newValue ? 'Izbor vijesti po državama uključen.' : 'Prikaz vraćen na sve balkanske vijesti.');
    } catch {
      showStatus('Greška prilikom spremanja postavke.', true);
    } finally {
      setSavingNewsPreference('');
    }
  };

  const handleNewsCountryToggle = async (country) => {
    const isEnabled = enabledNewsCountries.includes(country);
    const newCountries = isEnabled
      ? enabledNewsCountries.filter((item) => item !== country)
      : [...enabledNewsCountries, country].sort((a, b) => a.localeCompare(b));
    setSavingNewsPreference(country);
    try {
      const response = await apiCall('update-display-settings', {
        enabled_news_countries: newCountries,
      });
      if (!response.ok) throw new Error('Save failed');
      setEnabledNewsCountries(newCountries);
      showStatus(isEnabled ? `${country} isključena.` : `${country} uključena.`);
    } catch {
      showStatus('Greška prilikom spremanja postavke.', true);
    } finally {
      setSavingNewsPreference('');
    }
  };

  const handleSubcategoriesToggle = async () => {
    const newValue = !showSubcategories;
    setShowSubcategories(newValue);
    try {
      const response = await apiCall('update-subcategories', { show_subcategories: newValue });
      if (response.ok) {
        showStatus(newValue ? 'Subkategorije uključene.' : 'Subkategorije isključene.');
      } else {
        setShowSubcategories(!newValue);
        showStatus('Greška prilikom spremanja postavke.', true);
      }
    } catch {
      setShowSubcategories(!newValue);
      showStatus('Greška prilikom spremanja postavke.', true);
    }
  };

  const handleSubcategoryOnArticlesToggle = async () => {
    const newValue = !showSubcategoryOnArticles;
    setShowSubcategoryOnArticles(newValue);
    try {
      const response = await apiCall('update-display-settings', { show_subcategory_on_articles: newValue });
      if (response.ok) {
        showStatus(newValue ? 'Prikaz subkategorije na člancima uključen.' : 'Prikaz subkategorije na člancima isključen.');
      } else {
        setShowSubcategoryOnArticles(!newValue);
        showStatus('Greška prilikom spremanja postavke.', true);
      }
    } catch {
      setShowSubcategoryOnArticles(!newValue);
      showStatus('Greška prilikom spremanja postavke.', true);
    }
  };

  const handlePortalOnSubcategoriesToggle = async () => {
    const newValue = !showPortalOnSubcategories;
    setShowPortalOnSubcategories(newValue);
    try {
      const response = await apiCall('update-display-settings', { show_portal_on_subcategories: newValue });
      if (response.ok) {
        showStatus(newValue ? 'Prikaz portala uz subkategorije uključen.' : 'Prikaz portala uz subkategorije isključen.');
      } else {
        setShowPortalOnSubcategories(!newValue);
        showStatus('Greška prilikom spremanja postavke.', true);
      }
    } catch {
      setShowPortalOnSubcategories(!newValue);
      showStatus('Greška prilikom spremanja postavke.', true);
    }
  };

  const settingsOptions = [
    { key: 'korisnicki', title: 'Lični podaci' },
    { key: 'lozinka', title: 'Lozinka' },
    { key: 'strane-vijesti', title: 'Države i izvori' },
    { key: 'ostale-postavke', title: 'Pretraga i prikaz' },
  ];

  const renderPanel = () => {
    switch (activeSection) {
      case 'korisnicki':
        return (
          <form onSubmit={handleUserDataSubmit} className="postavke-form">
            <h2 className="panel-title">Promjena korisničkih podataka</h2>

            <div className="form-group">
              <label htmlFor="newName">Ime:</label>
              <input
                type="text"
                id="newName"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Unesite ime"
              />
            </div>

            <div className="form-group">
              <label>Datum rođenja:</label>
              <Datetime
                value={birthDate}
                onChange={(date) => setBirthDate(date)}
                timeFormat={false}
                dateFormat="DD.MM.YYYY"
                inputProps={{ placeholder: 'Odaberi datum rođenja' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPhone">Telefon:</label>
              <input
                type="tel"
                id="newPhone"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="Unesite telefon"
              />
            </div>

            <div className="form-group">
              <label>Spol:</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">Odaberi...</option>
                <option value="M">Muško</option>
                <option value="F">Žensko</option>
                <option value="O">Drugo</option>
              </select>
            </div>

            <button type="submit" className="submit-button">Spremi postavke</button>
          </form>
        );

      case 'lozinka':
        return (
          <form onSubmit={handlePasswordSubmit} className="postavke-form" noValidate>
            <h2 className="panel-title">Promijeni lozinku</h2>
            <div className="form-group">
              <label htmlFor="currentPassword">Trenutna lozinka:</label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">Nova lozinka:</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <small className="password-help">{PASSWORD_HELP_TEXT}</small>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Potvrdi novu lozinku:</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <button type="submit" className="submit-button">Promijeni lozinku</button>
          </form>
        );

      case 'strane-vijesti':
        return (
          <div className="postavke-form">
            <h2 className="panel-title">Prikaži strane vijesti</h2>
            <p className="toggle-description">
              Početni prikaz uključuje sve balkanske države, dok su Sjedinjene Američke Države i
              Ujedinjeno Kraljevstvo isključeni. Uključite opciju da sami izaberete države.
            </p>
            <div className="toggle-row">
              <span className="toggle-label" id="foreign-news-label">Prikaži strane vijesti</span>
              <button
                className={`toggle-switch ${showForeignNews ? 'active' : ''}`}
                onClick={handleForeignNewsToggle}
                disabled={Boolean(savingNewsPreference)}
                type="button"
                role="switch"
                aria-checked={showForeignNews}
                aria-labelledby="foreign-news-label"
              >
                <span className="toggle-knob" />
              </button>
            </div>
            {showForeignNews && (
              <div className="country-toggle-list" aria-label="Izbor država">
                <p className="toggle-description country-toggle-description">
                  Isključena država nestaje iz filtera. Opcija „Bez filtera“ zaobilazi izbor država.
                </p>
                {availableNewsCountries.map((country) => {
                  const isEnabled = enabledNewsCountries.includes(country);
                  const labelId = `news-country-${country.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
                  return (
                    <div className="toggle-row country-toggle-row" key={country}>
                      <span className="toggle-label" id={labelId}>{country}</span>
                      <button
                        className={`toggle-switch ${isEnabled ? 'active' : ''}`}
                        onClick={() => handleNewsCountryToggle(country)}
                        disabled={Boolean(savingNewsPreference)}
                        type="button"
                        role="switch"
                        aria-checked={isEnabled}
                        aria-labelledby={labelId}
                      >
                        <span className="toggle-knob" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'ostale-postavke':
        return (
          <div className="postavke-form">
            <h2 className="panel-title">Pretraga i prikaz</h2>
            <div className="toggle-row search-setting-row">
              <span className="toggle-label">Traži naslove kroz sve kategorije i portale</span>
                <InfoPopover title="Pretraga bez filtera"><p>Opcija „Bez filtera“ traži naslove kroz sve članke i zanemaruje sve filtere: kategorije, portale, države i vrijeme. Pretragu pokrećete dugmetom s lupom.</p></InfoPopover>
              <button type="button" aria-label="Traži naslove kroz sve kategorije i portale" aria-pressed={searchAllEnabled}
                className={`toggle-switch ${searchAllEnabled ? 'active' : ''}`}
                onClick={async () => {
                  try {
                    const response = await apiCall('update-display-settings', { search_all_enabled: !searchAllEnabled });
                    if (!response.ok) throw new Error();
                    setSearchAllEnabled(!searchAllEnabled);
                    showStatus('Postavka pretrage spremljena.');
                  } catch { showStatus('Greška pri spremanju postavke.', true); }
                }}><span className="toggle-knob" /></button>
            </div>

            <div className="toggle-row search-setting-row">
              <span className="toggle-label">Omogući više ključnih riječi u pretrazi</span>
                <InfoPopover title="Više ključnih riječi"><p>Enter dodaje cijelu riječ ili frazu kao oznaku. Dugme „Bilo koja / Sve“ bira podudaranje pojmova, a dugme s lupom pokreće pretragu.</p></InfoPopover>
              <button type="button" aria-label="Omogući više ključnih riječi u pretrazi" aria-pressed={multiKeywordEnabled}
                className={`toggle-switch ${multiKeywordEnabled ? 'active' : ''}`}
                onClick={async () => {
                  try {
                    const response = await apiCall('update-display-settings', { multi_keyword_enabled: !multiKeywordEnabled });
                    if (!response.ok) throw new Error();
                    setMultiKeywordEnabled(!multiKeywordEnabled);
                    showStatus('Postavka pretrage spremljena.');
                  } catch { showStatus('Greška pri spremanju postavke.', true); }
                }}><span className="toggle-knob" /></button>
            </div>
            <div className="toggle-row">
              <div className="toggle-copy"><span className="toggle-label">Prikaži subkategorije</span><p className="toggle-description">Uključite subkategorije kako biste mogli filtrirati artikle po detaljnijim kategorijama.</p></div>
              <button
                role="switch" aria-label="Prikaži subkategorije" aria-checked={showSubcategories}
                className={`toggle-switch ${showSubcategories ? 'active' : ''}`}
                onClick={handleSubcategoriesToggle}
                type="button"
              >
                <span className="toggle-knob" />
              </button>
            </div>

            <div className="toggle-row">
              <div className="toggle-copy"><span className="toggle-label">Prikaži subkategoriju na člancima</span><p className="toggle-description">Prikazuje subkategoriju kojoj članak pripada na samoj kartici članka.</p></div>
              <button
                role="switch" aria-label="Prikaži subkategoriju na člancima" aria-checked={showSubcategoryOnArticles}
                className={`toggle-switch ${showSubcategoryOnArticles ? 'active' : ''}`}
                onClick={handleSubcategoryOnArticlesToggle}
                type="button"
              >
                <span className="toggle-knob" />
              </button>
            </div>

            <div className="toggle-row">
              <div className="toggle-copy"><span className="toggle-label">Prikaži portal uz subkategorije</span><p className="toggle-description">Prikazuje uz svaku subkategoriju kojem portalu pripada.</p></div>
              <button
                role="switch" aria-label="Prikaži portal uz subkategorije" aria-checked={showPortalOnSubcategories}
                className={`toggle-switch ${showPortalOnSubcategories ? 'active' : ''}`}
                onClick={handlePortalOnSubcategoriesToggle}
                type="button"
              >
                <span className="toggle-knob" />
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="postavke-container">
      <LogoTray />
      <div className="postavke-content">
        <PageHeading eyebrow="Vaš račun" title="Postavke">Prilagodite podatke i način na koji pratite vijesti.</PageHeading>
        <div className="postavke-layout">
          <div className="postavke-sidebar">
            {settingsOptions.map((option) => (
              <button
                key={option.key}
                className={`settings-option-button ${activeSection === option.key ? 'active' : ''}`}
                onClick={() => setActiveSection(option.key)}
              >
                {option.title}
              </button>
            ))}
          </div>
          <div className="postavke-panel">
            {renderPanel()}
            {activeSection === 'korisnicki' && <DeleteAccount />}
            <p><Link to="/privacy">Politika privatnosti</Link></p>
          </div>
        </div>
      </div>
      {status && (
        <div className="status-toast" role="status" aria-live="polite">
          <span className="status-toast-text">{status}</span>
          <button
            type="button"
            className="status-toast-close"
            onClick={() => setStatus('')}
            aria-label="Zatvori"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export default Postavke;
